import { createAuthClient } from "better-auth/react";

/**
 * Better Auth client for browser-side authentication.
 *
 * NOTE: We cannot import auth types from @designs-by-maryam/auth as it
 * pulls in server-only code (node:crypto). The additional user fields
 * (role) are typed manually below.
 */

// User role type matching backend enum
export type UserRole = "admin" | "designer" | "client";

// Extended user type with role field
export type AppUser = {
	id: string;
	createdAt: Date;
	updatedAt: Date;
	email: string;
	emailVerified: boolean;
	name: string;
	image?: string | null;
	role: UserRole;
};

// Extended session type
export type AppSession = {
	user: AppUser;
	session: {
		id: string;
		createdAt: Date;
		updatedAt: Date;
		userId: string;
		expiresAt: Date;
		token: string;
		ipAddress?: string | null;
		userAgent?: string | null;
	};
};

const baseClient = createAuthClient({
	// Use same-origin by default, fallback to env var for external auth server
	baseURL: import.meta.env.VITE_SERVER_URL ?? "",
});

// Export typed auth client with extended session types
export const authClient = {
	...baseClient,
	useSession: () => {
		const result = baseClient.useSession();
		return result as typeof result & {
			data: AppSession | null;
		};
	},
	getSession: async (options?: Parameters<typeof baseClient.getSession>[0]) => {
		const result = await baseClient.getSession(options);
		return result as typeof result & {
			data: AppSession | null;
		};
	},
};
